import React, { useState, useMemo } from 'react';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, ToggleButton, Typography } from '@mui/material';
import { BuffOrDebuff, EquippableMemberRole, MemberRole, WarbandMemberDTO } from '../../types';
import { PopoverItem } from '../../hooks/useItemPopover';
import { useTreasureItemActions } from '../../hooks/useTreasureItemActions';
import { useDirtyTrackingStore } from '../../stores/dirtyTrackingStore';
import { useWarbandStore } from '../../stores/warbandStore';
import { useAuthStore } from '../../stores/authStore';
import { useReferenceDataStore } from '../../stores/referenceDataStore';
import { treasureToggleSx } from '../../App';
import { hashStringToSeed, generateBloodSpatterSVG, createSubtleIceSurface } from '../../theme/surfaces';
import { getSplitStatValue } from '../../utils/splitStats';
import { formatBuffEffect, getBuffName, getBuffSourceDescription } from '../../utils/formatting';
import { EditNameDialog } from './EditNameDialog';
import { MemberCard } from './MemberCard';
import { MemberStatsAndHealth } from './MemberStatsAndHealth';
import { MemberWeapons } from './MemberWeapons';
import { MemberPermanentInjuries } from './MemberPermanentInjuries';
import { MemberStateToggles } from './MemberStateToggles';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { ManageBuffsDebuffsModal } from '../modals/ManageBuffsDebuffsModal';

export interface WarbandMemberCardProps {
  member: WarbandMemberDTO;
  title?: string;
  isDefeated?: boolean;
  titleActionContent?: React.ReactNode;
  titleMenuItems?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
  }[];
  bottomRightActionContent?: React.ReactNode;
  showArmourWhenEmpty?: boolean;
  onItemClick: (event: React.MouseEvent<HTMLElement>, item: PopoverItem) => void;
  editMode?: boolean;
  showStateToggles?: boolean;
  unusedLevelUps?: number;
  onApplyLevelUp?: (stat: string) => Promise<void>;
  levelUpBonuses?: { fight: number; shoot: number; will: number; health: number };
  /** Extra role-specific content rendered inside the card (e.g. captain level/tricks). */
  extraDetails?: React.ReactNode;
}

export const WarbandMemberCard = React.memo(function WarbandMemberCard({
  member,
  title = member.name,
  isDefeated = false,
  titleActionContent,
  titleMenuItems = [],
  bottomRightActionContent,
  showArmourWhenEmpty,
  onItemClick,
  editMode = false,
  showStateToggles = false,
  unusedLevelUps,
  onApplyLevelUp,
  levelUpBonuses,
  extraDetails,
}: WarbandMemberCardProps) {
  const { warbandId, id: memberId, creatureName } = member;

  const memberRole: MemberRole =
    member.creatureId?.toLowerCase() === 'wizard' ? 'wizard'
      : member.creatureId?.toLowerCase() === 'apprentice' ? 'apprentice'
        : member.creatureId?.toLowerCase() === 'captain' ? 'captain'
          : 'soldier';

  const updateMemberBuffs = useDirtyTrackingStore((s) => s.updateMemberBuffs);
  const updateMemberNotes = useDirtyTrackingStore((s) => s.updateMemberNotes);
  const updateMemberWeapons = useDirtyTrackingStore((s) => s.updateMemberWeapons);
  const updateMemberArmours = useDirtyTrackingStore((s) => s.updateMemberArmours);
  const updateMemberTreasureItems = useDirtyTrackingStore((s) => s.updateMemberTreasureItems);
  const updateMemberName = useDirtyTrackingStore((s) => s.updateMemberName);
  const updateMemberHealth = useDirtyTrackingStore((s) => s.updateMemberHealth);
  const toggleMemberCarryingTreasure = useDirtyTrackingStore((s) => s.toggleMemberCarryingTreasure);
  const toggleMemberPoisoned = useDirtyTrackingStore((s) => s.toggleMemberPoisoned);
  const updateMemberPermanentInjuries = useDirtyTrackingStore((s) => s.updateMemberPermanentInjuries);
  const warbandBuffs = useWarbandStore((s) => s.warbandBuffs);
  const accessToken = useAuthStore((s) => s.accessToken);
  const getCreatureById = useReferenceDataStore((s) => s.getCreatureById);

  const creature = getCreatureById(member.creatureId);
  const canCarryTreasure = (creature?.canCarryTreasure ?? 0) !== 0;
  const traits = creature?.traits ?? [];

  // Weapon name override: treasure items that are based on a weapon use the treasure item's name/description.
  const treasureItems = member.treasureItems ?? [];
  const weapons = (member.weapons ?? []).map((w) => {
    const idx = treasureItems.findIndex((t) => t.baseItemId === w.id);
    return idx !== -1
      ? { ...w, name: treasureItems[idx].name, description: treasureItems[idx].description }
      : { ...w, description: '' };
  });
  const armours = member.armours ?? [];

  const [isEditNameOpen, setIsEditNameOpen] = useState(false);
  const [manageBuffsOpen, setManageBuffsOpen] = useState(false);
  const [buffPendingDelete, setBuffPendingDelete] = useState<BuffOrDebuff | null>(null);
  const [buffStackChooseOpen, setBuffStackChooseOpen] = useState(false);

  const maxHealth = getSplitStatValue(member, 'health', member.health + (levelUpBonuses?.health ?? 0), warbandBuffs);
  const sortedBuffs = [...(member.buffsAndDebuffs ?? [])].sort((a, b) => {
    if (a.source === 'spell' && b.source !== 'spell') return 1;
    if (a.source !== 'spell' && b.source === 'spell') return -1;
    return getBuffName(a).localeCompare(getBuffName(b));
  });
  const updateBuffIds = (buffIds: string[]) => updateMemberBuffs(memberId, memberRole, buffIds);

  const bloodSpatterUrl = useMemo(() => generateBloodSpatterSVG(hashStringToSeed(memberId)), [memberId]);
  const frostedGlassCardSx = useMemo(() => createSubtleIceSurface(hashStringToSeed(memberId), {}), [memberId]);

  const { openTreasureItemPopover, treasureItemDialogs } = useTreasureItemActions({ member, memberRole, warbandBuffs });
  // In play mode, treasure items open the use-capable popover instead of the generic one
  const handleItemClick = (event: React.MouseEvent<HTMLElement>, item: PopoverItem) => {
    const treasureItem = !editMode ? treasureItems.find((t) => t.id === item.id) : undefined;
    if (treasureItem) openTreasureItemPopover(event, treasureItem);
    else onItemClick(event, item);
  };

  return <MemberCard
    warbandId={warbandId}
    memberId={memberId}
    name={title}
    subtitle={creatureName}
    accessToken={accessToken}
    editMode={editMode}
    onEdit={() => setIsEditNameOpen(true)}
    actionMenuItems={titleMenuItems}
    statusContent={<>{!editMode && member.leftTable && <Chip label="Left Table" size="small" color="warning" />}{member.isTemporary && <Chip label="Temporary" size="small" color="info" />}</>}
    statsContent={<MemberStatsAndHealth member={member} editMode={editMode} levelUpBonuses={levelUpBonuses} unusedLevelUps={unusedLevelUps} onApplyLevelUp={onApplyLevelUp} warbandBuffs={warbandBuffs} />}
    health={{ current: Math.min(member.currentHealth, maxHealth), max: maxHealth, onChange: (next) => updateMemberHealth(memberId, memberRole, next) }}
    injuriesContent={<MemberPermanentInjuries injuries={member.permanentInjuries ?? []} editMode={editMode} memberRole={memberRole} member={member} onUpdateInjuries={(injuryIds) => updateMemberPermanentInjuries(memberId, memberRole as EquippableMemberRole, injuryIds)} />}
    lowerContent={<>
      <MemberWeapons weapons={weapons} sectionTitle="Equipment" editMode={editMode} memberRole={memberRole} memberId={memberId} memberName={member.name} onItemClick={handleItemClick} onSaveWeapons={(weaponIds) => updateMemberWeapons(memberId, memberRole as EquippableMemberRole, weaponIds)} armours={armours} onSaveArmours={(armourIds) => updateMemberArmours(memberId, memberRole as EquippableMemberRole, armourIds)} magicItems={treasureItems} onSaveMagicItems={(itemIds) => updateMemberTreasureItems(memberId, memberRole, itemIds)} />
      {extraDetails}
    </>}
    traits={traits}
    effects={sortedBuffs.map((buffOrDebuff) => {
      const count = buffOrDebuff.stackCount ?? 1;
      return {
        id: buffOrDebuff.id,
        name: count > 1 ? `${getBuffName(buffOrDebuff)} ×${count}` : getBuffName(buffOrDebuff),
        modifiers: formatBuffEffect(buffOrDebuff),
        getDescription: () => getBuffSourceDescription(member.warbandId, buffOrDebuff),
        onDelete:
          (buffOrDebuff.source === 'spell' || (buffOrDebuff.source === 'injury' && editMode && memberRole !== 'soldier'))
            ? () => {
              setBuffPendingDelete(buffOrDebuff);
              if (count > 1) setBuffStackChooseOpen(true);
            }
            : undefined,
      };
    })}
    onAddEffect={() => setManageBuffsOpen(true)}
    addEffectDisabled={isDefeated}
    footerContent={(!editMode || bottomRightActionContent) ? <>{showStateToggles && <MemberStateToggles sx={{ mr: 'auto', mb: 0 }} toggles={[
      { id: 'poisoned', label: 'Poisoned', selected: Boolean(member.poisoned), onToggle: () => toggleMemberPoisoned(memberId, memberRole) },
    ]} />}{!editMode && canCarryTreasure && <ToggleButton value="carrying-treasure" selected={member.carryingTreasure} onChange={() => toggleMemberCarryingTreasure(memberId, memberRole)} size="small" aria-label="carrying treasure" sx={treasureToggleSx}>Treasure</ToggleButton>}{!editMode && !canCarryTreasure && <Typography variant="body2" color="textSecondary" sx={{ alignSelf: 'center' }}>Cannot carry treasure</Typography>}{bottomRightActionContent}</> : undefined}
    notes={member.notes}
    onSaveNotes={(newNotes) => updateMemberNotes(memberId, memberRole, newNotes)}
    cardSx={frostedGlassCardSx}
    overlay={isDefeated ? <Box aria-hidden style={{ position: 'absolute', inset: 0, zIndex: 2 }} sx={{ borderRadius: 'inherit', pointerEvents: 'none', backgroundImage: bloodSpatterUrl, backgroundRepeat: 'no-repeat', backgroundSize: 'cover', backgroundPosition: 'center' }} /> : undefined}
  >
      {treasureItemDialogs}
      <ManageBuffsDebuffsModal
        open={manageBuffsOpen}
        editMode={editMode}
        member={member}
        memberRole={memberRole}
        onClose={() => setManageBuffsOpen(false)}
      />

      <ConfirmActionDialog
        open={buffPendingDelete !== null && !buffStackChooseOpen}
        title="Remove buff / debuff"
        message={buffPendingDelete ? `Remove "${getBuffName(buffPendingDelete)}" from ${member.name}?` : ''}
        confirmLabel="Remove"
        confirmColor="error"
        onClose={() => setBuffPendingDelete(null)}
        onConfirm={() => {
          if (!buffPendingDelete) return;
          const remaining = (member.buffsAndDebuffs ?? [])
            .filter((b) => b.id !== buffPendingDelete.id)
            .flatMap((b) => Array(b.stackCount ?? 1).fill(b.id));
          updateBuffIds(remaining);
          setBuffPendingDelete(null);
        }}
      />

      {/* Multi-stack member buff removal */}
      <Dialog
        open={buffStackChooseOpen}
        onClose={() => { setBuffPendingDelete(null); setBuffStackChooseOpen(false); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove buff / debuff</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {buffPendingDelete
              ? `"${getBuffName(buffPendingDelete)}" has ${buffPendingDelete.stackCount ?? 1} stacks on ${member.name}. How many would you like to remove?`
              : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setBuffPendingDelete(null); setBuffStackChooseOpen(false); }}>Cancel</Button>
          <Button
            color="warning"
            onClick={() => {
              if (!buffPendingDelete) return;
              const allIds = (member.buffsAndDebuffs ?? []).flatMap((b) => Array(b.stackCount ?? 1).fill(b.id));
              const idx = allIds.lastIndexOf(buffPendingDelete.id);
              const remaining = [...allIds.slice(0, idx), ...allIds.slice(idx + 1)];
              updateBuffIds(remaining);
              setBuffPendingDelete(null);
              setBuffStackChooseOpen(false);
            }}
          >
            Remove one stack
          </Button>
          <Button
            color="error"
            onClick={() => {
              if (!buffPendingDelete) return;
              const remaining = (member.buffsAndDebuffs ?? [])
                .filter((b) => b.id !== buffPendingDelete.id)
                .flatMap((b) => Array(b.stackCount ?? 1).fill(b.id));
              updateBuffIds(remaining);
              setBuffPendingDelete(null);
              setBuffStackChooseOpen(false);
            }}
          >
            Remove all stacks
          </Button>
        </DialogActions>
      </Dialog>

      <EditNameDialog
        open={isEditNameOpen}
        label="Name"
        initialValue={member.name}
        onClose={() => setIsEditNameOpen(false)}
        onSave={(newName) => {
          updateMemberName(memberId, memberRole, newName);
          setIsEditNameOpen(false);
        }}
      />
    </MemberCard>
  ;
});